<template>
  <div class="profile-page">
    <LoadingBar v-if="loading" message="Loading Vendor..." />

    <div v-else class="container-xl">
      <!-- Breadcrumb -->
      <nav class="profile-breadcrumb" aria-label="breadcrumb">
        <a href="#" @click.prevent="$router.push('/vendor/list')">
          <i class="fa fa-arrow-left"></i> Vendors
        </a>
        <span class="sep">/</span>
        <span class="current">{{ vendor.first_name }} {{ vendor.last_name }}</span>
      </nav>

      <!-- Header -->
      <div class="profile-header">
        <div class="profile-header-icon" style="background:var(--pp-warning-light);color:var(--pp-warning);">
          <img v-if="vendor.photo" :src="resolveFileUrl(vendor.photo)"
            style="width:100%;height:100%;object-fit:cover;border-radius:12px;" alt="Vendor" />
          <span v-else style="font-size:28px;font-weight:600;">{{ initials }}</span>
        </div>
        <div class="profile-header-body">
          <h3>{{ vendor.first_name }} {{ vendor.last_name }}</h3>
          <div class="profile-header-meta">
            <span><i class="fa fa-hashtag"></i>{{ vendor.external_id || 'N/A' }} </span>
            <span v-if="vendor.get_profile?.company_name">
              <i class="fa fa-building"></i>{{ vendor.get_profile.company_name }}
            </span>
            <span v-if="vendor.email"><i class="fa fa-envelope"></i>{{ vendor.email }} {{ vendor.status }}</span>
          </div>
        </div>
        <div class="profile-actions">
          <VendorStatus :status="vendor.status || 'pending'" @click="openStatusModal" />
          <button type="button" class="action-btn action-btn-back" @click="$router.push('/vendor/list')">
            <i class="fa fa-arrow-left"></i> Back
          </button>
          <button type="button" class="action-btn action-btn-primary" @click.prevent="edit(vendor)">
            <i class="fa fa-pencil"></i> Edit
          </button>
        </div>
      </div>

      <!-- Stat bar -->
      <div class="profile-stats">
        <div class="profile-stat-card">
          <div class="profile-stat-icon c-warning">
            <i class="fa fa-address-book"></i>
          </div>
          <div>
            <div class="profile-stat-label">Contacts</div>
            <div class="profile-stat-value">{{ contactList.length }}</div>
          </div>
        </div>
        <div class="profile-stat-card">
          <div class="profile-stat-icon c-accent">
            <i class="fa fa-map-marker-alt"></i>
          </div>
          <div>
            <div class="profile-stat-label">Locations</div>
            <div class="profile-stat-value">{{ locationList.length }}</div>
          </div>
        </div>
        <div class="profile-stat-card">
          <div class="profile-stat-icon c-info">
            <i class="fa fa-cogs"></i>
          </div>
          <div>
            <div class="profile-stat-label">Materials</div>
            <div class="profile-stat-value">{{ materialCount }}</div>
          </div>
        </div>
        <div class="profile-stat-card">
          <div class="profile-stat-icon c-success">
            <i class="fa fa-calendar"></i>
          </div>
          <div>
            <div class="profile-stat-label">Created</div>
            <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
              {{ formatDate(vendor.created_at) || 'N/A' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Main layout -->
      <div class="row g-3">
        <!-- Left Column -->
        <div class="col-lg-8 d-flex flex-column gap-3">
          <!-- Vendor Type Section -->
          <div class="profile-card">
            <div class="profile-card-header">
              <div class="profile-section-title">
                <i class="fa fa-user-tag"></i> Vendor Details
              </div>
            </div>
            <div class="profile-card-body">
              <div class="profile-field-grid">
                <div class="profile-field">
                  <span class="profile-label">Vendor Type</span>
                  <span class="profile-value">
                    <span class="profile-badge" :class="vendorType === 'contractor' ? 'c-warning' : 'c-info'">
                      {{ vendorType ? vendorType.toUpperCase() : 'N/A' }}
                    </span>
                  </span>
                </div>

                <!-- Vendor Specific Fields -->
                <template v-if="vendorType === 'vendor'">
                  <div class="profile-field">
                    <span class="profile-label">Nature</span>
                    <span class="profile-value">
                      <span v-for="(nature, idx) in vendorNatureList" :key="idx" 
                        class="profile-badge c-info" style="font-size:11px;margin:2px;">
                        {{ capitalize(nature) }}
                      </span>
                      <span v-if="!vendorNatureList.length" class="text-muted">N/A</span>
                    </span>
                  </div>
                  <div class="profile-field" v-if="profileData?.vendor_nature_other">
                    <span class="profile-label">Other Nature</span>
                    <span class="profile-value">{{ profileData.vendor_nature_other }}</span>
                  </div>
                  <div class="profile-field">
                    <span class="profile-label">Risk Rating</span>
                    <span class="profile-value">
                      <span class="risk-badge" :class="getRiskClass(profileData?.vendor_risk_rating)">
                        {{ profileData?.vendor_risk_rating?.toUpperCase() || 'N/A' }}
                      </span>
                    </span>
                  </div>
                  <div class="profile-field">
                    <span class="profile-label">Qualification Status</span>
                    <span class="profile-value">
                      <span class="status-badge" :class="getStatusClass(profileData?.qualification_status)">
                        {{ profileData?.qualification_status?.toUpperCase() || 'N/A' }}
                      </span>
                    </span>
                  </div>
                  <div class="profile-field" v-if="profileData?.description">
                    <span class="profile-label">Description</span>
                    <span class="profile-value">{{ profileData.description }}</span>
                  </div>
                </template>

                <!-- Contractor Specific Fields -->
                <template v-if="vendorType === 'contractor'">
                  <div class="profile-field">
                    <span class="profile-label">Nature</span>
                    <span class="profile-value">
                      <span v-for="(nature, idx) in contractorNatureList" :key="idx" 
                        class="profile-badge c-warning" style="font-size:11px;margin:2px;">
                        {{ capitalize(nature) }}
                      </span>
                      <span v-if="!contractorNatureList.length" class="text-muted">N/A</span>
                    </span>
                  </div>
                  <div class="profile-field" v-if="profileData?.contractor_nature_other">
                    <span class="profile-label">Other Nature</span>
                    <span class="profile-value">{{ profileData.contractor_nature_other }}</span>
                  </div>
                  <div class="profile-field">
                    <span class="profile-label">Risk Rating</span>
                    <span class="profile-value">
                      <span class="risk-badge" :class="getRiskClass(profileData?.contractor_risk_rating)">
                        {{ profileData?.contractor_risk_rating?.toUpperCase() || 'N/A' }}
                      </span>
                    </span>
                  </div>
                  <div class="profile-field" v-if="profileData?.description">
                    <span class="profile-label">Description</span>
                    <span class="profile-value">{{ profileData.description }}</span>
                  </div>
                </template>

                <div class="profile-field">
                  <span class="profile-label">Status</span>
                  <span class="profile-value">
                     <VendorStatus :status="vendor.status || 'pending'" @click="openStatusModal" />
                     
                  </span>
                  
                  <span v-if="vendor.remarks != null && vendor.remarks !== ''">
                    <i class="fa fa-info-circle"></i> {{ vendor.remarks }}
                  </span>
                </div>
                <div class="profile-field">
                  <span class="profile-label">Created</span>
                  <span class="profile-value">{{ formatDate(vendor.created_at) }}</span>
                </div>
                <div class="profile-field">
                  <span class="profile-label">Updated</span>
                  <span class="profile-value">{{ formatDate(vendor.updated_at) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Qualification Documents (Vendor Only) -->
          <div class="profile-card" v-if="vendorType === 'vendor' && qualificationDocuments.length">
            <div class="profile-card-header">
              <div class="profile-section-title" style="color:var(--pp-amber-text);">
                <i class="fa fa-certificate" style="color:var(--pp-amber-text);"></i> Qualification Documents
              </div>
              <span class="profile-count-pill">{{ qualificationDocuments.length }}</span>
            </div>
            <div class="profile-card-body">
              <div class="d-flex flex-wrap gap-2">
                <div v-for="(doc, index) in qualificationDocuments" :key="index"
                  class="file-item d-flex align-items-center gap-2 p-2 border rounded" style="flex:1 1 200px;">
                  <i class="fa fa-file-pdf" style="font-size:20px;color:var(--pp-danger);"></i>
                  <div class="flex-grow-1" style="min-width:0;">
                    <div class="profile-value" style="font-size:12px;font-weight:500;word-break:break-word;">
                      {{ doc.file_name || 'Document ' + (index + 1) }}
                    </div>
                    <div style="font-size:10px;color:var(--pp-text-muted);">
                      {{ formatFileSize(doc.file_size) }}
                    </div>
                  </div>
                  <button type="button" class="btn btn-sm btn-outline-primary hover-white" 
                    @click="openFileViewer(doc.file_path, doc.file_name)">
                    <i class="fa fa-eye"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Contacts -->
          <div class="profile-card" v-if="contactList.length">
            <div class="profile-card-header">
              <div class="profile-section-title" style="color:var(--pp-warning-text);">
                <i class="fa fa-address-book" style="color:var(--pp-warning-text);"></i> Contacts
              </div>
              <span class="profile-count-pill">{{ contactList.length }}</span>
            </div>
            <div class="profile-card-body p-0">
              <div class="table-responsive">
                <table class="profile-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(contact, index) in contactList" :key="index">
                      <td>
                        <span class="profile-value" style="font-size:13px;font-weight:500;">
                          {{ contact.name || 'N/A' }}
                        </span>
                      </td>
                      <td>
                        <a v-if="contact.email" :href="`mailto:${contact.email}`"
                          style="color:var(--pp-accent);text-decoration:none;font-size:13px;">
                          {{ contact.email }}
                        </a>
                        <span v-else class="profile-value text-muted" style="font-size:13px;">N/A</span>
                      </td>
                      <td>
                        <a v-if="contact.phone" :href="`tel:${contact.phone}`"
                          style="color:var(--pp-accent);text-decoration:none;font-size:13px;">
                          {{ contact.phone }}
                        </a>
                        <span v-else class="profile-value text-muted" style="font-size:13px;">N/A</span>
                      </td>
                      <td>
                        <span class="profile-value text-muted" style="font-size:13px;">
                          {{ contact.role || 'N/A' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Images -->
          <div class="profile-card" v-if="imageList.length">
            <div class="profile-card-header">
              <div class="profile-section-title">
                <i class="fa fa-images"></i> Images
              </div>
              <span class="profile-count-pill">{{ imageList.length }}</span>
            </div>
            <div class="profile-card-body">
              <div class="row g-2">
                <div v-for="(image, index) in imageList" :key="index" class="col-6 col-sm-4 col-md-3">
                  <div class="profile-attachment">
                    <div class="profile-attachment-thumb" style="height:100px;background:var(--pp-bg-alt);cursor:pointer;border-radius:8px;overflow:hidden;">
                      <img v-if="image.file_path"
                        :src="resolveFileUrl(image.file_path)"
                        :alt="image.file_name || 'Image'"
                        style="width:100%;height:100%;object-fit:cover;"
                        @click="openImageViewer(image.file_path)" />
                      <div v-else style="display:flex;align-items:center;justify-content:center;height:100%;">
                        <i class="fa fa-image" style="font-size:24px;color:var(--pp-text-muted);"></i>
                      </div>
                    </div>
                    <div class="profile-attachment-meta">
                      <div class="profile-attachment-name">{{ image.file_name || 'Image ' + (index + 1) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Checklist (Contractor Only) -->
          <div class="profile-card" v-if="vendorType === 'contractor' && checklistItems.length">
            <div class="profile-card-header">
              <div class="profile-section-title" style="color:var(--pp-warning);">
                <i class="fa fa-clipboard-check" style="color:var(--pp-warning);"></i> Compliance Checklist
              </div>
              <span class="profile-count-pill">{{ checklistItems.length }}</span>
            </div>
            <div class="profile-card-body">
              <div v-for="(item, index) in checklistItems" :key="item.id" 
                class="checklist-item mb-2 p-2 border rounded"
                :class="{
                  'border-success': item.status === 'yes',
                  'border-danger': item.status === 'no',
                  'border-warning': item.status === 'na'
                }">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <div class="fw-bold" style="font-size:13px;">
                      <span class="text-muted me-1">{{ index + 1 }}.</span>
                      {{ item.checklist_item?.item_name || 'Item' }}
                    </div>
                    <div v-if="item.status === 'na' && item.comments" 
                      style="font-size:12px;color:var(--pp-text-muted);">
                      <i class="fa fa-comment me-1"></i>{{ item.comments }}
                    </div>
                    <!-- Checklist Documents -->
                    <div v-if="item.documents && item.documents.length" class="mt-1">
                      <div v-for="doc in item.documents" :key="doc.id" 
                        class="d-flex align-items-center gap-2" style="font-size:12px;">
                        <i class="fa fa-paperclip text-muted"></i>
                        <span class="text-muted">{{ doc.document_name }}</span>
                        <button type="button" class="btn btn-link p-0 text-primary" 
                          style="font-size:11px;text-decoration:none;" 
                          @click="openFileViewer(doc.document_path, doc.document_name)">
                          <i class="fa fa-eye"></i> View
                        </button>
                      </div>
                    </div>
                  </div>
                  <span class="status-badge" :class="getChecklistStatusClass(item.status)" style="flex-shrink:0;margin-left:8px;">
                    {{ item.status?.toUpperCase() || 'N/A' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="col-lg-4 d-flex flex-column gap-3">
          <!-- Locations -->
          <div class="profile-card" v-if="locationList.length">
            <div class="profile-card-header">
              <div class="profile-section-title" style="color:var(--pp-accent);">
                <i class="fa fa-map-marker-alt" style="color:var(--pp-accent);"></i> Locations
              </div>
              <span class="profile-count-pill">{{ locationList.length }}</span>
            </div>
            <div class="profile-card-body">
              <div class="d-flex flex-wrap gap-2">
                <span v-for="location in locationList" :key="location.get_location?.id || location.id"
                  class="profile-badge c-accent" style="font-size:12px;">
                  <i class="fa fa-map-marker-alt me-1"></i>
                  {{ location.get_location?.name || 'Unknown location' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Materials (Grouped by Type) -->
          <div class="profile-card" v-if="materialList.length">
            <div class="profile-card-header">
              <div class="profile-section-title" style="color:var(--pp-info);">
                <i class="fa fa-cubes" style="color:var(--pp-info);"></i> Materials
              </div>
              <span class="profile-count-pill">{{ materialList.length }}</span>
            </div>
            <div class="profile-card-body">
              <div v-for="(group, type) in groupedMaterials" :key="type" class="mb-2">
                <div class="fw-bold text-muted small mb-1">{{ capitalize(type) }}</div>
                <div class="d-flex flex-wrap gap-1">
                  <span v-for="material in group" :key="material.id"
                    class="profile-badge c-info" style="font-size:11px;">
                    <i class="fa fa-cube me-1"></i>
                    {{ material.name || material.title || 'Unnamed' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Assets -->
          <div class="profile-card" v-if="assetList.length">
            <div class="profile-card-header">
              <div class="profile-section-title" style="color:var(--pp-primary);">
                <i class="fa fa-microchip" style="color:var(--pp-primary);"></i> Assets
              </div>
              <span class="profile-count-pill">{{ assetList.length }}</span>
            </div>
            <div class="profile-card-body">
              <div class="d-flex flex-wrap gap-2">
                <span v-for="asset in assetList" :key="asset.id"
                  class="profile-badge c-primary" style="font-size:12px;">
                  <i class="fa fa-microchip me-1"></i>
                  {{ asset.name || 'Unnamed Asset' }}
                </span>
              </div>
            </div>
          </div>

          <!-- General Files -->
          <div class="profile-card" v-if="generalFiles.length">
            <div class="profile-card-header">
              <div class="profile-section-title" style="color:var(--pp-amber-text);">
                <i class="fa fa-file-alt" style="color:var(--pp-amber-text);"></i> Files & Documents
              </div>
              <span class="profile-count-pill">{{ generalFiles.length }}</span>
            </div>
            <div class="profile-card-body">
              <div class="d-flex flex-column gap-2">
                <div v-for="(file, index) in generalFiles" :key="index"
                  class="d-flex align-items-center justify-content-between p-2 border rounded">
                  <div class="d-flex align-items-center gap-2" style="min-width:0;">
                    <i :class="getFileIcon(file.file_type)" 
                      style="font-size:16px;color:var(--pp-text-muted);flex-shrink:0;"></i>
                    <div style="min-width:0;">
                      <div class="profile-value" style="font-size:12px;font-weight:500;word-break:break-word;">
                        {{ file.file_name || 'Document ' + (index + 1) }}
                      </div>
                      <div style="font-size:10px;color:var(--pp-text-muted);">
                        {{ formatFileType(file.file_type) }} • {{ formatFileSize(file.file_size) }}
                      </div>
                    </div>
                  </div>
                  <div class="d-flex gap-1 flex-shrink-0">
                    <button type="button" class="btn btn-sm btn-outline-primary hover-white" 
                      @click="openFileViewer(file.file_path, file.file_name)">
                      <i class="fa fa-eye"></i>
                    </button>
                    <a v-if="file.file_path" :href="resolveFileUrl(file.file_path)" target="_blank"
                      class="btn btn-sm btn-outline-secondary">
                      <i class="fa fa-download"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div class="profile-card"
            v-if="!locationList.length && !materialList.length && !assetList.length && !generalFiles.length && !contactList.length">
            <div class="profile-card-header">
              <div class="profile-section-title">
                <i class="fa fa-plug"></i> Connections
              </div>
            </div>
            <div class="profile-card-body">
              <div class="profile-empty">
                <i class="fa fa-plug"></i>
                <p>No connections yet</p>
                <span style="font-size:12px;color:var(--pp-text-muted);">
                  This vendor has no linked items
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- File Viewer Modal -->
    <div class="file-viewer-modal" v-if="showFileViewer" @click.self="closeFileViewer">
      <div class="file-viewer-content">
        <div class="file-viewer-header">
          <span class="file-viewer-title">{{ viewerFileName }}</span>
          <button type="button" class="btn-close" @click="closeFileViewer"></button>
        </div>
        <div class="file-viewer-body">
          <iframe v-if="isPdf" :src="viewerFileUrl" class="file-viewer-iframe"></iframe>
          <img v-else-if="isImage" :src="viewerFileUrl" class="file-viewer-image" />
          <div v-else class="file-viewer-fallback">
            <i class="fa fa-file-alt" style="font-size:48px;color:var(--pp-text-muted);"></i>
            <p>Preview not available</p>
            <a :href="viewerFileUrl" target="_blank" class="btn btn-primary">
              <i class="fa fa-download"></i> Download File
            </a>
          </div>
        </div>
      </div>
    </div>

    <FormModal ref="editModal" title="Edit Vendor" id="edit_vendor_form_modal">
      <EditVendor v-if="editingVendorId" :key="editingVendorId" :vendor-id="editingVendorId"
        ref="editVendorForm" @updated="onVendorUpdated" @close="closeEditModal('editModal')" />
    </FormModal>

    <FormModal ref="statusModal" title="Update Vendor Status" id="status_modal">
      <form @submit.prevent="submitStatusUpdate">
        <div class="mb-4">
          <label class="form-label form-label-thin">Status</label>
          <MultiSelect
            v-model="statusUpdate.status"
            :options="statusOptions"
            option-value="value"
            option-label="label"
            name="status"
            id="status"
            ref="status"
            placeholder="Select status..."
            :multiple="false"
          />
          <div v-if="statusErrors.status" class="text-danger small mt-1">
            {{ statusErrors.status[0] }}
          </div>
        </div>

        <div class="mb-4" v-if="requiresStatusRemarks">
          <label class="form-label form-label-thin">Remarks</label>
          <textarea class="form-control form-control-thin" rows="3" v-model="statusUpdate.remarks"
            placeholder="Enter remarks"></textarea>
          <div v-if="statusErrors.remarks" class="text-danger small mt-1">
            {{ statusErrors.remarks[0] }}
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeStatusModal">
            Cancel
          </button>
          <button type="submit" class="btn btn-sm btn-primary" :disabled="statusSubmitting">
            {{ statusSubmitting ? 'Updating...' : 'Update Status' }}
          </button>
        </div>
      </form>
    </FormModal>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import EditVendor from '@/views/Vendor/EditVendor.vue'
import MultiSelect from '@/components/MultiSelect.vue'
import VendorStatus from '@/components/VendorStatus.vue'

export default {
  name: 'VendorProfile',
  components: {
    LoadingBar,
    FormModal,
    EditVendor,
    MultiSelect,
    VendorStatus
  },
  data() {
    return {
      vendor: {},
      loading: false,
      external_id: this.$route.params.external_id,
      editingVendorId: null,
      showFileViewer: false,
      viewerFileUrl: '',
      viewerFileName: '',
      statusSubmitting: false,
      statusErrors: {},
      statusUpdate: {
        status: '',
        remarks: ''
      },
      statusOptions: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'conditionaly_approved', label: 'Conditionaly Approved' },
        { value: 'rejected', label: 'Rejected' }
      ]
    }
  },
  computed: {
    initials() {
      const f = this.vendor.first_name?.[0] || ''
      const l = this.vendor.last_name?.[0] || ''
      return (f + l).toUpperCase()
    },
    profileData() {
      return this.vendor.get_profile || {}
    },
    vendorType() {
      return this.profileData?.vendor_type || null
    },
    vendorNatureList() {
      const nature = this.profileData?.vendor_nature
      if (!nature) return []
      if (Array.isArray(nature)) return nature
      try { return JSON.parse(nature) } catch (e) { return [] }
    },
    contractorNatureList() {
      const nature = this.profileData?.contractor_nature
      if (!nature) return []
      if (Array.isArray(nature)) return nature
      try { return JSON.parse(nature) } catch (e) { return [] }
    },
    contactList() {
      return this.vendor.get_contacts || this.vendor.contacts || []
    },
    imageList() {
      const images = this.vendor.get_images || this.vendor.images || []
      return images.filter(img => img.attachment_type === 'Vendor Profile')
    },
    qualificationDocuments() {
      const files = [
        ...(this.vendor.get_files || this.vendor.files || []),
        ...(this.vendor.get_images || this.vendor.images || []),
        ...(this.vendor.qualification_documents || [])
      ]
      return files.filter(f => {
        const type = String(f.attachment_type || f.type || '').toLowerCase()
        return type === 'qualification document' || type === 'qualification_document'
      })
    },
    generalFiles() {
      const files = [
        ...(this.vendor.get_files || this.vendor.files || []),
        ...(this.vendor.get_images || this.vendor.images || [])
      ]
      return files.filter(f => {
        const type = String(f.attachment_type || f.type || '').toLowerCase()
        return type !== 'qualification document' && type !== 'qualification_document'
      })
    },
    fileList() {
      return [
        ...(this.vendor.get_files || this.vendor.files || []),
        ...(this.vendor.get_images || this.vendor.images || [])
      ]
    },
    locationList() {
      const rawLocations = this.vendor.get_locations || this.vendor.locations || []
      return rawLocations.map(location => location.get_location ? location : { get_location: location })
    },
    materialList() {
      return this.vendor.get_materials || []
    },
    assetList() {
      return this.vendor.get_assets || []
    },
    materialCount() {
      return this.materialList.length
    },
    groupedMaterials() {
      const groups = {}
      this.materialList.forEach(m => {
        const type = m.type || 'other'
        if (!groups[type]) groups[type] = []
        groups[type].push(m)
      })
      return groups
    },
    checklistItems() {
      return this.vendor.vendor_checklists || []
    },
    isPdf() {
      return this.viewerFileName?.toLowerCase().endsWith('.pdf')
    },
    isImage() {
      const ext = this.viewerFileName?.toLowerCase().split('.').pop()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
    },
    requiresStatusRemarks() {
      return ['rejected', 'conditionaly_approved'].includes(String(this.statusUpdate.status || '').toLowerCase())
    }
  },
  mounted() {
    this.fetchVendor()
  },
  methods: {
    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    },
    formatFileSize(bytes) {
      if (!bytes) return 'Unknown size'
      const kb = bytes / 1024
      if (kb < 1024) return `${kb.toFixed(1)} KB`
      return `${(kb / 1024).toFixed(1)} MB`
    },
    formatFileType(type) {
      if (!type) return 'Unknown type'
      return type.replace('application/', '').replace('image/', '').toUpperCase()
    },
    getFileIcon(fileType) {
      if (!fileType) return 'fa fa-file'
      if (fileType.includes('pdf')) return 'fa fa-file-pdf'
      if (fileType.includes('word') || fileType.includes('document')) return 'fa fa-file-word'
      if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'fa fa-file-excel'
      if (fileType.includes('image')) return 'fa fa-file-image'
      if (fileType.includes('text')) return 'fa fa-file-text'
      return 'fa fa-file'
    },
    getRiskClass(rating) {
      if (!rating) return 'risk-na'
      const map = {
        'critical': 'risk-critical',
        'high': 'risk-high',
        'medium': 'risk-medium',
        'low': 'risk-low'
      }
      return map[rating] || 'risk-na'
    },
    getStatusClass(status) {
      if (!status) return 'status-na'
      const map = {
        'yes': 'status-yes',
        'no': 'status-no',
        'na': 'status-na'
      }
      return map[status] || 'status-na'
    },
    getChecklistStatusClass(status) {
      if (!status) return 'status-na'
      const map = {
        'yes': 'status-yes',
        'no': 'status-no',
        'na': 'status-na'
      }
      return map[status] || 'status-na'
    },
    resolveFileUrl(path) {
      if (!path) return ''
      if (/^https?:\/\//i.test(path)) return path
      const host = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      if (path.startsWith('/')) {
        return `${host}${path}`
      }
      return `${host}/${path}`
    },
    async fetchVendor() {
      this.loading = true
      const result = await form_api_submit({
        url: `vendor/profile/${this.external_id}`,
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })
      if (result?.success) {
        this.vendor = result.data || {}
      }
      this.loading = false
    },
    openEditModal(ref) {
      this.$refs[ref].showModal()
    },
    closeEditModal(ref) {
      this.$refs[ref].closeModal()
      this.editingVendorId = null
    },
    edit(user) {
      this.editingVendorId = user.id
      this.openEditModal('editModal')
    },
    async onVendorUpdated() {
      await this.fetchVendor()
    },
    openFileViewer(path, name) {
      this.viewerFileUrl = this.resolveFileUrl(path)
      this.viewerFileName = name || 'Document'
      this.showFileViewer = true
    },
    openImageViewer(path) {
      this.viewerFileUrl = this.resolveFileUrl(path)
      this.viewerFileName = 'Image'
      this.showFileViewer = true
    },
    closeFileViewer() {
      this.showFileViewer = false
      this.viewerFileUrl = ''
      this.viewerFileName = ''
    },
    openStatusModal() {
      this.statusErrors = {}
      this.statusUpdate.status = this.vendor.status || 'pending'
      this.statusUpdate.remarks = ''
      this.$refs.statusModal.showModal()
    },
    closeStatusModal() {
      this.$refs.statusModal.closeModal()
      this.statusErrors = {}
      this.statusUpdate.status = ''
      this.statusUpdate.remarks = ''
    },
    async submitStatusUpdate() {
      this.statusSubmitting = true
      this.statusErrors = {}

      if (!this.statusUpdate.status) {
        this.statusErrors.status = ['Please select a status']
      }

      const needsRemarks = ['rejected', 'conditionaly_approved'].includes(this.statusUpdate.status)
      if (needsRemarks && !this.statusUpdate.remarks.trim()) {
        this.statusErrors.remarks = ['Remarks are required for this status']
      }

      if (Object.keys(this.statusErrors).length) {
        this.statusSubmitting = false
        return
      }

      const result = await form_api_submit({
        url: `vendor/status/${this.vendor.id}`,
        method: 'POST',
        data: {
          status: this.statusUpdate.status,
          remarks: this.statusUpdate.remarks
        },
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result?.success) {
        this.closeStatusModal()
        await this.fetchVendor()
      } else if (result?.errors) {
        this.statusErrors = result.errors
      }

      this.statusSubmitting = false
    }
  }
}
</script>

<style scoped>
/* Risk Badge Styles */
.risk-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #fff;
}
.risk-critical { background: #6f42c1; }
.risk-high { background: #dc3545; }
.risk-medium { background: #fd7e14; color: #212529; }
.risk-low { background: #198754; }
.risk-na { background: #6c757d; }

/* Status Badge Styles */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}
.status-yes { background: #d4edda; color: #155724; }
.status-no { background: #f8d7da; color: #721c24; }
.status-na { background: #fff3cd; color: #856404; }

/* Checklist Border Colors */
.border-success { border-left: 4px solid #28a745 !important; }
.border-danger { border-left: 4px solid #dc3545 !important; }
.border-warning { border-left: 4px solid #ffc107 !important; }

/* Profile Badge Colors */
.c-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
.c-danger { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
.c-warning { background: #fff3cd; color: #856404; border: 1px solid #ffeaa7; }
.c-info { background: #d1ecf1; color: #0c5460; border: 1px solid #bee5eb; }
.c-accent { background: #cce5ff; color: #004085; border: 1px solid #b8daff; }
.c-primary { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }

/* File Viewer Modal */
.file-viewer-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}
.file-viewer-content {
  background: #fff;
  border-radius: 12px;
  max-width: 90%;
  max-height: 90%;
  width: 900px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.file-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}
.file-viewer-title {
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.file-viewer-body {
  flex: 1;
  padding: 20px;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}
.file-viewer-iframe {
  width: 100%;
  height: 600px;
  border: none;
}
.file-viewer-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
.file-viewer-fallback {
  text-align: center;
  padding: 40px;
}
.file-viewer-fallback p {
  margin: 16px 0;
  color: #6b7280;
}

/* Profile Card Styles */
.profile-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  overflow: visible;
}
.profile-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 14px 14px 0 0;
}
.profile-card-body {
  padding: 16px 20px;
}
.profile-field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
}
.profile-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 5px;
  border-bottom: 1px solid #f3f4f6;
}
.profile-field:last-child {
  border-bottom: none;
}
.profile-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.profile-value {
  font-size: 14px;
  color: #111827;
}
.profile-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.profile-badge .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.profile-count-pill {
  background: #e5e7eb;
  color: #374151;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.profile-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #374151;
}
.profile-empty {
  text-align: center;
  padding: 24px 16px;
  color: #6b7280;
}
.profile-empty i {
  font-size: 32px;
  color: #d1d5db;
  margin-bottom: 8px;
}
.profile-empty p {
  margin: 4px 0;
  font-weight: 500;
}

/* Profile Table */
.profile-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.profile-table thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.profile-table th {
  padding: 10px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.profile-table td {
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
}
.profile-table tbody tr:hover {
  background: #f9fafb;
}

/* Profile Stats */
.profile-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}
.profile-stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}
.profile-stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.profile-stat-label {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.profile-stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

/* Profile Header */
.profile-header {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  padding: 16px 20px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
}
.profile-header-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.profile-header-body {
  flex: 1;
}
.profile-header-body h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}
.profile-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #6b7280;
}
.profile-header-meta i {
  margin-right: 4px;
}
.profile-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.action-btn {
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.action-btn:hover {
  background: #f3f4f6;
}
.action-btn-primary {
  background: #4f46e5;
  color: #fff;
  border-color: #4f46e5;
}
.action-btn-primary:hover {
  background: #4338ca;
  border-color: #4338ca;
  color: #fff;
}
.action-btn-back {
  background: #f3f4f6;
  border-color: #e5e7eb;
}
.action-btn-back:hover {
  background: #e5e7eb;
}
.action-btn-sm {
  padding: 4px 10px;
  font-size: 12px;
}
.btn-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px;
}
.btn-close:hover {
  color: #000;
}

/* Profile Attachment */
.profile-attachment {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.profile-attachment-thumb {
  position: relative;
  overflow: hidden;
}
.profile-attachment-meta {
  padding: 6px 8px;
  text-align: center;
}
.profile-attachment-name {
  font-size: 11px;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* File Item */
.file-item {
  background: #f9fafb;
  border-radius: 8px;
  transition: all 0.2s;
}
.file-item:hover {
  background: #f3f4f6;
}

/* Breadcrumb */
.profile-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  margin-bottom: 16px;
}
.profile-breadcrumb a {
  color: #4f46e5;
  text-decoration: none;
  font-weight: 500;
}
.profile-breadcrumb a:hover {
  color: #3730a3;
}
.profile-breadcrumb .sep {
  color: #d1d5db;
}
.profile-breadcrumb .current {
  color: #111827;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 768px) {
  .profile-header {
    flex-wrap: wrap;
  }
  .profile-actions {
    width: 100%;
    justify-content: flex-end;
  }
  .profile-field-grid {
    grid-template-columns: 1fr;
  }
  .profile-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  .file-viewer-content {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
  .file-viewer-iframe {
    height: 400px;
  }
}
</style>